import React, { useEffect, useState } from 'react';
import { verifyCertificate } from '../utils/api';
import { toast } from 'react-toastify';

const VerifyMember = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [error, setError] = useState(null);
  const [hasSearched, setHasSearched] = useState(false); // Track if a search has been performed

  useEffect(() => {
    document.title = 'Verify Member - KPCA Professional Cleaning Verification';
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Verify KPCA certified cleaning professionals, check membership status, and access member directory. Search by name, registration number, or location.');
    }
  }, []);

  const handleSearch = async () => {
    if (!searchTerm.trim()) {
      toast.error('Please enter a search term');
      return;
    }
    
    setIsSearching(true);
    setError(null);
    setHasSearched(true); // Mark that a search has been performed
    
    try {
      console.log('Searching for:', searchTerm);
      const response = await verifyCertificate(searchTerm);
      
      if (response.success) {
        setSearchResults(response.data || []);
        if (response.data && response.data.length > 0) {
          toast.success(`${response.data.length} certificate(s) found`);
        } else {
          toast.info('No company found matching your search criteria');
        }
      } else {
        setSearchResults([]);
        toast.warning(response.message || 'No certificates found');
      }
      
    } catch (error) {
      console.error('Search error:', error);
      setError(error.message);
      setSearchResults([]);
      toast.error(`Search failed: ${error.message}`);
    } finally {
      setIsSearching(false);
    }
  };

  const handleSearchTermChange = (e) => {
    const newSearchTerm = e.target.value;
    setSearchTerm(newSearchTerm);
    
    // Reset search state when the user clears the search term
    if (!newSearchTerm.trim()) {
      setHasSearched(false);
      setSearchResults([]);
      setError(null);
    }
  };

  const getStatusColor = (verificationResult) => {
    if (!verificationResult) return 'bg-gray-100 text-gray-800';
    
    switch (verificationResult.verification_status) {
      case 'VALID':
        return 'bg-green-100 text-green-800';
      case 'VALID_EXPIRING_SOON':
        return 'bg-yellow-100 text-yellow-800';
      case 'EXPIRED':
        return 'bg-red-100 text-red-800';
      case 'REVOKED':
        return 'bg-red-100 text-red-800';
      case 'SUSPENDED':
        return 'bg-orange-100 text-orange-800';
      case 'MEMBERSHIP_INACTIVE':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (verificationResult) => {
    if (!verificationResult) return '❓';
    
    switch (verificationResult.verification_status) {
      case 'VALID':
        return '✅';
      case 'VALID_EXPIRING_SOON':
        return '⚠️';
      case 'EXPIRED':
        return '❌';
      case 'REVOKED':
        return '🚫';
      case 'SUSPENDED':
        return '⏸️';
      case 'MEMBERSHIP_INACTIVE':
        return '⏸️';
      default:
        return '❓';
    }
  };

  return (
    <main className="min-h-screen">
     

      {/* Member Verification Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              🔎 Member Verification Database
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Search our database of certified cleaning professionals. Verify membership status, certifications, and contact information.
            </p>
          </div>

          {/* Search Interface */}
          <div className="max-w-2xl mx-auto mb-12">
            <div className="bg-gray-50 rounded-lg p-6">
              <div className="flex gap-4">
                <input
                  type="text"
                  placeholder="Search by company name, registration number, or KPCA certificate number..."
                  value={searchTerm}
                  onChange={handleSearchTermChange}
                  onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                  className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
                <button
                  onClick={handleSearch}
                  disabled={isSearching}
                  className="bg-primary-600 hover:bg-primary-700 text-white px-6 py-3 rounded-lg font-medium transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSearching ? '🔍 Searching...' : '🔍 Search'}
                </button>
              </div>
              <p className="text-sm text-gray-500 mt-2">
                💡 Tip: You can search by company name, registration number, or KPCA certificate number
              </p>
            </div>
          </div>

          {/* Error Display */}
          {error && (
            <div className="max-w-4xl mx-auto mb-6">
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <div className="flex items-center">
                  <span className="text-red-500 text-xl mr-3">❌</span>
                  <p className="text-red-700">{error}</p>
                </div>
              </div>
            </div>
          )}

          {/* Search Results */}
          {searchResults.length > 0 && (
            <div className="max-w-4xl mx-auto">
              <h3 className="text-xl font-bold text-gray-900 mb-6">
                Search Results ({searchResults.length} found)
              </h3>
              <div className="space-y-6">
                {searchResults.map((result, index) => (
                  <div key={index} className="bg-white border rounded-lg shadow-lg p-6">
                    <div className="flex justify-between items-start mb-4">
                      <div>
                        <h4 className="text-xl font-bold text-gray-900">{result.company_info?.company_name}</h4>
                        <p className="text-gray-600">Contact Person: {result.company_info?.contact_person}</p>
                      </div>
                      <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(result.verification_result)}`}>
                        {getStatusIcon(result.verification_result)} {result.verification_result?.verification_status?.replace('_', ' ') || 'Unknown'}
                      </span>
                    </div>

                    {/* Verification Message */}
                    <div className={`mb-4 p-3 rounded-lg ${result.verification_result?.is_valid ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
                      <p className={`text-sm ${result.verification_result?.is_valid ? 'text-green-700' : 'text-red-700'}`}>
                        <strong>Verification Status:</strong> {result.verification_result?.verification_message}
                      </p>
                    </div>

                    <div className="grid md:grid-cols-2 gap-4 mb-4">
                      <div>
                        <p className="text-sm text-gray-500">Registration Number</p>
                        <p className="font-medium">{result.company_info?.registration_number}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">KPCA Certificate #</p>
                        <p className="font-medium">{result.certificate_info?.certificate_number}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Certificate Type</p>
                        <p className="font-medium">{result.certificate_info?.certificate_type}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Expiry Date</p>
                        <p className="font-medium">
                          {result.certificate_info?.expiry_date}
                          {result.certificate_info?.days_until_expiry !== null && (
                            <span className={`ml-2 text-xs px-2 py-1 rounded ${
                              result.certificate_info.days_until_expiry > 30 ? 'bg-green-100 text-green-800' :
                              result.certificate_info.days_until_expiry > 0 ? 'bg-yellow-100 text-yellow-800' :
                              'bg-red-100 text-red-800'
                            }`}>
                              {result.certificate_info.days_until_expiry > 0 
                                ? `${result.certificate_info.days_until_expiry} days left`
                                : 'Expired'
                              }
                            </span>
                          )}
                        </p>
                      </div>
                    </div>

                    {result.company_info?.services_offered && result.company_info.services_offered.length > 0 && (
                      <div className="mb-4">
                        <p className="text-sm text-gray-500 mb-2">Services Offered</p>
                        <div className="flex flex-wrap gap-2">
                          {result.company_info.services_offered.map((service, serviceIndex) => (
                            <span key={serviceIndex} className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm">
                              {service}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    
                  </div>
                ))}
              </div>
            </div>
          )}

          {searchTerm && searchResults.length === 0 && !isSearching && !error && hasSearched && (
            <div className="text-center py-8">
              <div className="text-6xl mb-4">🔍</div>
              <p className="text-gray-500 text-lg">No company found matching your search criteria.</p>
              <p className="text-sm text-gray-400 mt-2">Try different keywords or contact us for assistance.</p>
            </div>
          )}
        </div>
      </section>

      {/* Contact for Verification */}
      <section className="py-12 bg-primary-600 text-white">
        <div className="container mx-auto px-6 text-center">
          <h3 className="text-2xl font-bold mb-4">Need Help with Verification?</h3>
          <p className="text-primary-100 mb-6 max-w-2xl mx-auto">
            Can't find what you're looking for? Our team is ready to help you verify member credentials 
            or provide additional information about our certified professionals.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="/contact"
              className="bg-white text-primary-600 hover:bg-gray-100 py-3 px-6 rounded-lg font-medium transition-colors duration-200"
            >
              📞 Contact Us
            </a>
            <a
              href="tel:+254213123456"
              className="bg-primary-700 hover:bg-primary-800 text-white py-3 px-6 rounded-lg font-medium transition-colors duration-200"
            >
              📱 Call Now: +254 716284637
            </a>
          </div>
        </div>
      </section>
    </main>
  );
};

export default VerifyMember; 